/**
 * LongRangePlan — typed calendar (Phase 117.A · Feb 2026).
 *
 * Replaces the previous unit-based grid. A Long Range Plan is the
 * teacher's general year-at-a-glance sketch drafted in September; it
 * is direction, not specifics. Each cell is free text the teacher
 * types into; the platform renders bullets and bold sub-headings
 * lightly. No units, expectation codes, summative chips, or linked
 * unit cards — those connections happen elsewhere (Gradebook,
 * Planner, Math Engine) but NOT here.
 *
 * Layout: subjects down × ten months across (Sept → Jun). Term 1
 * spans the first five columns, Term 2 the next five. A thin
 * "Progress Report" marker labels the Sept–Oct columns as a quiet
 * time-marker (not a column or planning division).
 *
 * Subject list: pulled from the teacher's Class Profile via the
 * canonical filter (Phase 116). Default-show-all when the profile
 * is empty.
 *
 * Storage: `long_range_plans_v2` collection — one document per
 * teacher per school year, `cells[subject_key][month_key] = text`.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, History, Download, ArrowRight, X } from "lucide-react";
import AppShell from "../components/shell/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import {
  getLrpYear,
  putLrpCell,
  currentSchoolYear,
  listLrpYears,
  copyLrpFromYear,
  parseSchoolYearParam,
} from "../lib/lrp";
import {
  CANONICAL_SUBJECTS,
  profileSubjectsAsKeys,
} from "../lib/subjects";

// ============================================================================
//  Constants
// ============================================================================

const MONTHS = [
  { key: "september", label: "September", term: 1, shortLabel: "Sep" },
  { key: "october", label: "October", term: 1, shortLabel: "Oct" },
  { key: "november", label: "November", term: 1, shortLabel: "Nov" },
  { key: "december", label: "December", term: 1, shortLabel: "Dec" },
  { key: "january", label: "January", term: 1, shortLabel: "Jan" },
  { key: "february", label: "February", term: 2, shortLabel: "Feb" },
  { key: "march", label: "March", term: 2, shortLabel: "Mar" },
  { key: "april", label: "April", term: 2, shortLabel: "Apr" },
  { key: "may", label: "May", term: 2, shortLabel: "May" },
  { key: "june", label: "June", term: 2, shortLabel: "Jun" },
];

const AUTOSAVE_DELAY_MS = 600;

// ============================================================================
//  Cell — typed-text editor with light markdown rendering
// ============================================================================

/**
 * Render a cell's text in view mode with light markdown:
 *   - lines starting with -, *, or • → bulleted list
 *   - lines wrapped in **…** → bold sub-heading
 *   - other non-empty lines → plain paragraph
 * No third-party deps; keeps the renderer auditable and tiny.
 */
const renderCellText = (raw) => {
  const text = (raw || "").trim();
  if (!text) return null;
  const lines = raw.split(/\r?\n/);
  const out = [];
  let bulletBuffer = [];
  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    out.push(
      <ul
        key={`ul-${out.length}`}
        className="list-disc pl-5 space-y-0.5 my-1 marker:text-[#4a6984]/50"
      >
        {bulletBuffer.map((b, i) => (
          <li key={i} className="text-[12px] leading-snug text-[#1c2d3d]">
            {b}
          </li>
        ))}
      </ul>,
    );
    bulletBuffer = [];
  };
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushBullets();
      continue;
    }
    if (/^[-*•]\s+/.test(line)) {
      bulletBuffer.push(line.replace(/^[-*•]\s+/, ""));
      continue;
    }
    flushBullets();
    const boldOnly = /^\*\*(.+)\*\*$/.exec(line);
    if (boldOnly) {
      out.push(
        <p
          key={`h-${out.length}`}
          className="font-extrabold text-[11px] uppercase tracking-[0.08em] text-[#1c2d3d] mt-1"
        >
          {boldOnly[1]}
        </p>,
      );
      continue;
    }
    out.push(
      <p
        key={`p-${out.length}`}
        className="text-[12px] leading-snug text-[#1c2d3d]"
      >
        {line}
      </p>,
    );
  }
  flushBullets();
  return out;
};

const Cell = ({ subjectKey, monthKey, initialText, onSave, savingFlag, readOnly }) => {
  const [text, setText] = useState(initialText || "");
  const [editing, setEditing] = useState(false);
  const taRef = useRef(null);
  const timerRef = useRef(null);

  // Keep local text in sync if the server doc gets reloaded (e.g.
  // school-year switch). Don't overwrite while the user is actively
  // typing in this cell — only sync when not editing.
  useEffect(() => {
    if (!editing) setText(initialText || "");
  }, [initialText, editing]);

  const enterEdit = () => {
    if (readOnly) return;
    setEditing(true);
    // Defer focus to next tick so the textarea has mounted.
    window.setTimeout(() => {
      const el = taRef.current;
      if (el) {
        el.focus();
        // Move cursor to end for fast appending.
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch {
          /* ignored */
        }
      }
    }, 0);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      onSave(subjectKey, monthKey, v);
    }, AUTOSAVE_DELAY_MS);
  };

  const handleBlur = () => {
    // Flush any pending debounced save immediately so leaving the
    // cell never loses keystrokes.
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
      onSave(subjectKey, monthKey, text);
    }
    setEditing(false);
  };

  const cellTestId = `lrp-cell-${subjectKey}-${monthKey}`;
  const hasContent = (text || "").trim().length > 0;

  if (editing) {
    return (
      <td
        className="align-top p-0 border-l border-[#4a6984]/10 min-w-[200px] w-[200px] bg-[#96ded1]/15"
        data-testid={cellTestId}
      >
        <textarea
          ref={taRef}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Type your plan…"
          className="w-full h-full min-h-[120px] resize-none bg-transparent p-2.5 text-[12px] leading-snug text-[#1c2d3d] focus:outline-none placeholder:text-[#4a6984]/40"
          data-testid={`${cellTestId}-textarea`}
        />
      </td>
    );
  }

  return (
    <td
      role={readOnly ? undefined : "button"}
      tabIndex={readOnly ? -1 : 0}
      onClick={readOnly ? undefined : enterEdit}
      onKeyDown={readOnly ? undefined : (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          enterEdit();
        }
      }}
      className={`align-top p-2.5 border-l border-[#4a6984]/10 min-w-[200px] w-[200px] ${readOnly ? "cursor-default" : "cursor-text hover:bg-[#f4f5f7]"} transition-colors ${hasContent ? "" : "bg-white"}`}
      data-testid={cellTestId}
    >
      {hasContent ? (
        <div className="space-y-0.5">{renderCellText(text)}</div>
      ) : readOnly ? (
        <p className="text-[11px] italic text-[#4a6984]/25">—</p>
      ) : (
        <p className="text-[11px] italic text-[#4a6984]/35">Type your plan…</p>
      )}
      {savingFlag ? (
        <p className="mt-1 text-[9px] uppercase tracking-[0.12em] text-[#4a6984]/45">
          saving…
        </p>
      ) : null}
    </td>
  );
};

// ============================================================================
//  Page
// ============================================================================

const LongRangePlan = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const currentYear = useMemo(() => currentSchoolYear(), []);
  // Honour `?school_year=` when present and valid. Malformed / out-of-range
  // values fall back silently to `currentYear` — the URL a teacher pastes
  // may be mangled by accident, so we fail-safe rather than blow up.
  const initialYear = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("school_year");
    return parseSchoolYearParam(raw) || currentYear;
  }, [location.search, currentYear]);
  const [schoolYear, setSchoolYear] = useState(initialYear);
  const [availableYears, setAvailableYears] = useState([]);
  // Years the teacher has an actual doc for (from listLrpYears). This
  // is DISTINCT from availableYears, which force-adds `currentYear`
  // and the current querystring target to the switcher for UX. The
  // copy-forward offer needs the pure "have a doc" set — otherwise it
  // could pick a year the teacher has never touched.
  const [yearsWithDocs, setYearsWithDocs] = useState([]);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingByCell, setSavingByCell] = useState({}); // {`${subj}|${month}`: true}
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Session-only dismissal of the copy-forward offer. Not persisted — the
  // teacher can navigate away and back to see it again in a new session.
  const [copyOfferDismissed, setCopyOfferDismissed] = useState(false);
  const [copyOfferBusy, setCopyOfferBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Founder decision (Jul 11 2026): every year in the switcher is the
  // teacher's own plan and is fully editable. There is no ARCHIVE gate.
  const isReadOnly = false;

  // Compute visible subjects from the Class Profile (canonical filter).
  const visibleSubjects = useMemo(() => {
    const set = profileSubjectsAsKeys(user?.subjects_taught || []);
    const isDefault = set.size === 0;
    return CANONICAL_SUBJECTS.filter((s) => isDefault || set.has(s.key));
  }, [user?.subjects_taught]);

  // Load the year + its doc whenever the school_year changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLrpYear(schoolYear)
      .then((d) => {
        if (cancelled) return;
        setDoc(d);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [schoolYear]);

  // Refresh the available-years list whenever the doc changes shape
  // (initial load, copy-from-year). We always make sure the current
  // year is in the list even if the teacher hasn't typed anything
  // yet so the switcher can offer it.
  const refreshYears = async () => {
    try {
      const years = await listLrpYears();
      const docsSet = new Set(years.map((y) => y.school_year).filter(Boolean));
      const withDocsSorted = [...docsSet].sort().reverse();
      setYearsWithDocs(withDocsSorted);
      const switcherSet = new Set(docsSet);
      switcherSet.add(currentYear);
      switcherSet.add(schoolYear);
      const sorted = [...switcherSet].sort().reverse();
      setAvailableYears(sorted);
    } catch {
      setYearsWithDocs([]);
      setAvailableYears(Array.from(new Set([currentYear, schoolYear])));
    }
  };
  useEffect(() => {
    refreshYears();
  }, []);

  const handleSave = async (subjectKey, monthKey, text) => {
    if (isReadOnly) return;
    const cellId = `${subjectKey}|${monthKey}`;
    setSavingByCell((p) => ({ ...p, [cellId]: true }));
    try {
      await putLrpCell({
        school_year: schoolYear,
        subject_key: subjectKey,
        month_key: monthKey,
        text,
      });
      setDoc((prev) => {
        if (!prev) return prev;
        const nextCells = { ...(prev.cells || {}) };
        nextCells[subjectKey] = { ...(nextCells[subjectKey] || {}), [monthKey]: text };
        return { ...prev, cells: nextCells };
      });
      setLastSavedAt(new Date());
    } catch {
      /* Errors here are silent on purpose — the next keystroke
         retries the save automatically. A toast on every transient
         failure would feel hostile during a noisy auto-save flow. */
    } finally {
      setSavingByCell((p) => {
        const n = { ...p };
        delete n[cellId];
        return n;
      });
    }
  };

  const handleCopyFrom = async (fromYear) => {
    if (!fromYear || fromYear === schoolYear) return;
    setCopyOfferBusy(true);
    try {
      await copyLrpFromYear({
        from_school_year: fromYear,
        to_school_year: schoolYear,
      });
      toast.success(`Copied plan from ${fromYear}`, {
        description: "Edit anything you'd like — auto-saves on every keystroke.",
      });
      // Reload the current year's doc so the copied cells appear.
      const fresh = await getLrpYear(schoolYear);
      setDoc(fresh);
      refreshYears();
      setCopyOfferDismissed(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't copy from that year";
      toast.error("Copy failed", { description: String(detail) });
    } finally {
      setCopyOfferBusy(false);
    }
  };

  const hasAnyContent = useMemo(() => {
    const cells = doc?.cells || {};
    for (const subj of Object.values(cells)) {
      if (!subj) continue;
      for (const v of Object.values(subj)) {
        if ((v || "").trim()) return true;
      }
    }
    return false;
  }, [doc]);

  // Copy-forward offer source: the most recent prior school year that
  // (a) is strictly before `schoolYear` and (b) has an LRP doc. If
  // `schoolYear` already has any typed content OR the teacher has
  // dismissed the offer for this session OR nothing prior exists, the
  // offer stays hidden.
  //
  // We source from `yearsWithDocs` (pure list of years the teacher
  // actually typed into) NOT `availableYears` — the latter force-adds
  // `currentYear` and the querystring target to the switcher, which
  // would let the offer point at a year the teacher never touched.
  const copyForwardSourceYear = useMemo(() => {
    if (hasAnyContent) return null;
    if (copyOfferDismissed) return null;
    for (const y of yearsWithDocs) {
      if (y < schoolYear) return y;
    }
    return null;
  }, [yearsWithDocs, schoolYear, hasAnyContent, copyOfferDismissed]);

  const handleExport = async () => {
    if (!hasAnyContent) {
      toast.error("Nothing to export yet", { description: "Type at least one cell before exporting." });
      return;
    }
    setExporting(true);
    try {
      const res = await api.get("/lrp/export.pdf", {
        params: { school_year: schoolYear },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AtomicEdu_LongRangePlan_${schoolYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Export failed";
      toast.error("Couldn't generate the PDF", { description: String(detail) });
    } finally {
      setExporting(false);
    }
  };

  if (!authLoading && !user) return <Navigate to="/" replace />;

  const cellsByPath = (doc?.cells || {});
  const lastSavedLabel = lastSavedAt
    ? lastSavedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : null;
  const yearLabel = schoolYear;

  return (
    <AppShell
      title="Long Range Plan"
      subtitle={`Your typed year-at-a-glance — ${yearLabel}`}
    >
      <div className="bg-[#f4f5f7] min-h-full">
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          {/* Header card */}
          <div className="ae-card p-5 mb-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#4a6984]/70">
                  Long Range Plan · {yearLabel}
                </p>
                <h1 className="mt-1 font-black text-2xl sm:text-3xl text-[#1c2d3d] tracking-tight">
                  Your year at a glance
                </h1>
                <p className="mt-1.5 text-sm text-[#4a6984] max-w-2xl leading-relaxed">
                  Click any cell and type. Use{" "}
                  <code className="px-1 rounded bg-[#f4f5f7] text-[11px] text-[#1c2d3d]">-</code>{" "}
                  for bullets or{" "}
                  <code className="px-1 rounded bg-[#f4f5f7] text-[11px] text-[#1c2d3d]">**Reading**</code>{" "}
                  for sub-headings. Every keystroke auto-saves.
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {availableYears.length > 0 ? (
                    <select
                      value={schoolYear}
                      onChange={(e) => setSchoolYear(e.target.value)}
                      data-testid="lrp-year-switcher"
                      className="h-9 rounded-md border border-[#4a6984]/25 bg-white px-3 text-xs font-bold text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                    >
                      {availableYears.map((y) => (
                        <option key={y} value={y}>
                          {y === currentYear ? `${y} (current)` : y}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleExport}
                    disabled={exporting || !hasAnyContent}
                    data-testid="lrp-export-pdf-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#1c2d3d] text-white text-xs font-bold hover:bg-[#0e1a25] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {exporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    {exporting ? "Exporting…" : "Export PDF"}
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#4a6984]" data-testid="lrp-saved-status">
                  {isReadOnly ? (
                    <span className="text-[#4a6984]/50 italic">Archive — view only</span>
                  ) : lastSavedLabel ? (
                    <>
                      <Save className="h-3.5 w-3.5 text-[#4a6984]/60" />
                      <span>Saved at {lastSavedLabel}</span>
                    </>
                  ) : (
                    <span className="text-[#4a6984]/50 italic">Auto-saves on every edit</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Copy-forward offer: shown only when the current view is
              completely empty AND the teacher has an LRP doc for the
              most recent prior year. Session-only dismissal. The
              backend route blocks itself if the target has any
              content, so overwrite is impossible by construction. */}
          {copyForwardSourceYear ? (
            <div
              className="ae-card p-4 mb-5 bg-[#96ded1]/15 border border-[#96ded1]/40"
              data-testid="lrp-copy-forward-offer"
              role="region"
              aria-label="Copy previous year's plan"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-[#1c2d3d] leading-snug">
                  You have a <strong>{copyForwardSourceYear}</strong> long-range
                  plan. Copy it into <strong>{schoolYear}</strong> as a starting
                  point?
                </p>
                <button
                  type="button"
                  onClick={() => setCopyOfferDismissed(true)}
                  aria-label="Dismiss offer"
                  data-testid="lrp-copy-forward-close"
                  className="shrink-0 rounded p-1 text-[#4a6984]/60 hover:text-[#1c2d3d] hover:bg-[#96ded1]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#96ded1] transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleCopyFrom(copyForwardSourceYear)}
                  disabled={copyOfferBusy}
                  data-testid="lrp-copy-forward-accept"
                  className="ae-cta hover:ae-cta h-9 px-4 rounded-md font-extrabold inline-flex items-center gap-1.5 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {copyOfferBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <ArrowRight className="h-3.5 w-3.5" />
                  )}
                  Copy it over
                </button>
                <button
                  type="button"
                  onClick={() => setCopyOfferDismissed(true)}
                  disabled={copyOfferBusy}
                  data-testid="lrp-copy-forward-dismiss"
                  className="inline-flex items-center h-9 px-4 rounded-md border-2 border-[#4a6984]/25 bg-white text-sm font-extrabold text-[#1c2d3d] hover:border-[#1c2d3d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Start fresh
                </button>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="ae-card p-10 flex items-center justify-center text-[#4a6984]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading your plan…
            </div>
          ) : visibleSubjects.length === 0 ? (
            <div className="ae-card p-10 text-center">
              <History className="h-6 w-6 mx-auto mb-3 text-[#4a6984]/60" />
              <p className="text-sm text-[#4a6984]">
                No subjects in your Class Profile. Open your profile from the
                sidebar to pick which subjects you teach — they&apos;ll appear here.
              </p>
            </div>
          ) : (
            <div
              className="ae-card overflow-hidden"
              data-testid="lrp-grid-wrapper"
            >
              <div className="overflow-x-auto">
                <table
                  className="w-full border-collapse"
                  data-testid="lrp-grid"
                >
                  <thead>
                    {/* Term headers spanning 5 columns each */}
                    <tr className="bg-[#1c2d3d] text-white">
                      <th className="sticky left-0 z-20 bg-[#1c2d3d] w-[180px] min-w-[180px] px-3 py-3 text-left">
                        <span className="font-bold uppercase text-[10px] tracking-[0.22em] text-white/55">
                          Subject
                        </span>
                      </th>
                      <th
                        colSpan={5}
                        className="px-3 py-3 text-center border-l border-white/10"
                        data-testid="lrp-term1-header"
                      >
                        <p className="font-black text-sm tracking-tight">Term 1</p>
                        <p className="text-[10px] text-white/55 uppercase tracking-[0.18em]">
                          September – January
                        </p>
                      </th>
                      <th
                        colSpan={5}
                        className="px-3 py-3 text-center border-l border-white/10"
                        data-testid="lrp-term2-header"
                      >
                        <p className="font-black text-sm tracking-tight">Term 2</p>
                        <p className="text-[10px] text-white/55 uppercase tracking-[0.18em]">
                          February – June
                        </p>
                      </th>
                    </tr>
                    {/* Progress Report marker over Sept–Oct */}
                    <tr className="bg-[#96ded1]/20">
                      <th className="sticky left-0 z-20 bg-[#96ded1]/20" />
                      <th
                        colSpan={2}
                        className="px-3 py-1.5 text-center border-l border-[#4a6984]/15"
                        data-testid="lrp-progress-report-marker"
                      >
                        <span className="font-bold uppercase text-[9px] tracking-[0.22em] text-[#1c2d3d]">
                          Progress Report
                        </span>
                      </th>
                      <th colSpan={8} />
                    </tr>
                    {/* Month columns */}
                    <tr className="bg-[#f4f5f7] border-y border-[#4a6984]/15">
                      <th className="sticky left-0 z-20 bg-[#f4f5f7] w-[180px] min-w-[180px] px-3 py-2" />
                      {MONTHS.map((m) => (
                        <th
                          key={m.key}
                          className="px-3 py-2 border-l border-[#4a6984]/10 min-w-[200px] w-[200px]"
                          data-testid={`lrp-month-header-${m.key}`}
                        >
                          <p className="font-extrabold text-xs text-[#1c2d3d] tracking-tight text-left">
                            {m.label}
                          </p>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSubjects.map((s) => (
                      <tr
                        key={s.key}
                        className="border-t border-[#4a6984]/10"
                        data-testid={`lrp-row-${s.key}`}
                      >
                        <th
                          scope="row"
                          className="sticky left-0 z-10 bg-white w-[180px] min-w-[180px] px-3 py-3 text-left align-top border-r border-[#4a6984]/15"
                        >
                          <p className="font-extrabold text-xs text-[#1c2d3d] tracking-tight uppercase tracking-[0.06em]">
                            {s.label}
                          </p>
                          {s.group ? (
                            <p className="text-[9px] uppercase tracking-[0.18em] text-[#4a6984]/55 mt-1">
                              {s.group}
                            </p>
                          ) : null}
                        </th>
                        {MONTHS.map((m) => {
                          const cellId = `${s.key}|${m.key}`;
                          const initialText = cellsByPath[s.key]?.[m.key] || "";
                          return (
                            <Cell
                              key={m.key}
                              subjectKey={s.key}
                              monthKey={m.key}
                              initialText={initialText}
                              onSave={handleSave}
                              savingFlag={!!savingByCell[cellId]}
                              readOnly={isReadOnly}
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="mt-4 text-xs text-[#4a6984]/65 leading-relaxed max-w-3xl">
            This is your general year direction — high-level themes, units,
            and strands. Specific lessons, summative tasks, and assessment
            live in your Generator, Math Engine, Planner, and Gradebook.
          </p>
        </main>
      </div>
    </AppShell>
  );
};

export default LongRangePlan;
